import * as React from "react";
import {cx} from "emotion";

import "./address-sub-section.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Load2}            from "@Common/react/load2";
import {Static2}          from "@Common/react/static-2";
import {Badge}            from "@Common/ui-components/heavy/common/design2/badge/badge";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}   from "@Common/ui-components/light/scroll-into-view";

import {AddShippingAddressModal}  from "../../../../account/common/shipping-addresses-panel/modals/add-shipping-address-modal/add-shipping-address-modal";
import {EditShippingAddressModal} from "../../../../account/common/shipping-addresses-panel/modals/edit-shipping-address-modal/edit-shipping-address-modal";
import {SelectAddressModal}       from "../../../../customize/steps/checkout/shipping-section/common/select-address-modal/select-address-modal";
import {LoadingPanel}             from "../../../loading-panel/loading-panel";
import {SubSectionBody}           from "../common/sub-section-body/sub-section-body";

export const AddressSubSection = (
    {
        addressData: {myAddresses, officeAddress},
        agentId=undefined,
        onChangeAddressId,
        selectedAddressId,
        loading=false,
        next,
    }
) => cs(
    consumeContext("apis"),
    consumeContext("userLevels"),

    ["addAddressModal", ({}, next) => AddShippingAddressModal({
        agentId,
        next,
    })],
    ["selectAddressModal", ({}, next) => SelectAddressModal({
        myAddresses, officeAddress,
        agentId,
        selectedAddressId,
        next,
    })],
    ["editAddressModal", ({}, next) => EditShippingAddressModal({agentId, next})],
    ["address", ({}, next) => next(
        [officeAddress.value, ...myAddresses.value || []].filter(v=>v).find((a) => a.idPrimary === selectedAddressId)
    )],
    ["totalAddresses", ({}, next) => next(
        [officeAddress.value, ...myAddresses.value || []].filter(v=>v).filter((q, idx, v) => v.findIndex(obj => obj.idPrimary === q.idPrimary) === idx)
    )],

    ["validation", ({apis, address}, next) => {
        if (!address) {
            return Load2({
                disabled: true,
                next,
            });
        }
        if (
            address.countryCode &&
            address.countryCode.toUpperCase() !== "US" &&
            address.countryCode.toUpperCase() !== "USA"
        ) {
            return Load2({
                fetch: async () => true,
                next,
            });
        }

        const addr1 = address.address1 + "; " + address.city + "; " + address.state + " " + address.postalCode + "; " + address.countryCode
        return Load2({
            _key: addr1,
            fetch: async () => (
                (await apis.next.shipping.validateAddress(addr1))
                    ?.[0]?.DeliveryPointValidation === "CONFIRMED"
            ),
            next,
        });
    }],

    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],
    ({
        selectAddressModal, addAddressModal, editAddressModal, address,
        validation,
        totalAddresses, userLevels, eh, domRef
    }) => next({
        loading: loading || validation.loading,
        invalid: !address || !validation.value,
        highlightErrors: () => {
            eh.start();
            scrollIntoView(domRef.get());
        },
        renderAfter: selectAddressModal.render,
        render: ({}) => {
            const Actions = ({different}) => (
                <div className="actions">
                    <a
                        xd-qa-type="link"
                        xd-qa-name="Select a different address"
                        onClick={() => {
                            if(userLevels?.isGuest) {
                                alert("Something went wrong. Please refresh the page.")
                            } else {
                                selectAddressModal.show({
                                    onChangeAddressId,
                                });
                            }

                        }}
                    >
                        {[
                            "Select",
                            different ? "a different" : "an",
                            "address",
                            "(" +
                            (
                                (totalAddresses?.length ?? 0)
                            ) +
                            ")",
                        ].join(" ")}
                    </a>

                    <a
                        xd-qa-type="link"
                        xd-qa-name="Add New Address"
                        onClick={() => {
                            if(userLevels?.isGuest) {
                                alert("Something went wrong. Please refresh the page.")
                            } else {
                                addAddressModal.show({
                                    onDone: async (address) => {
                                        myAddresses.onChange([
                                            address,
                                            ...myAddresses.value,
                                        ]);
                                        onChangeAddressId(address.idPrimary);
                                    }
                                });
                            }
                        }}
                    >Add New Address</a>
                </div>
            );

            return (
                <div className={cx("address-sub-section-5tf", eh.css)} ref={domRef.set}>
                    <div className="header" xd-qa-type="label" xd-qa-name="Specify Ship to Address">
                        Specify Ship to address
                    </div>


                    {(myAddresses.loading || officeAddress.loading) ? (
                        <div className="loading" xd-qa-type="label" xd-qa-name="Shipping Loading">
                            Loading...
                        </div>
                    ) : !address ? (
                        loading ? (
                            <div className="loading" xd-qa-type="label" xd-qa-name="Shipping Loading">
                                Loading...
                            </div>
                        ) : !selectedAddressId ? (
                            <div className="no-data">
                                <div className="message">
                                    No address selected.
                                </div>

                                {Actions({different: false})}
                            </div>
                        ) : (
                            <div className="address-3c0">
                                <div className="error" xd-qa-type="label" xd-qa-name="Select New Address">
                                    The selected shipping address is no longer available. Please select a new address.
                                </div>

                                {Actions({different: false})}
                            </div>
                        )
                    ) : (
                        SubSectionBody({
                            content: (
                                <div className="address-3c0">
                                    <div className="line1">
                                        <div className="name" xd-qa-type="label" xd-qa-name={address.contactName}>
                                            {address.contactName}
                                        </div>

                                        {officeAddress.value !== address && (
                                            <div className="actions">
                                                <a
                                                    xd-qa-type="link"
                                                    xd-qa-name="Edit Address"
                                                    onClick={() => editAddressModal.show({
                                                        lockedAsDefault: address.defaultBranch,
                                                        address,
                                                        onDone: async (address) => {
                                                            myAddresses.onChange(myAddresses.value.map((a) => a.idPrimary !== address.idPrimary ? a : address));
                                                            onChangeAddressId(address.idPrimary);
                                                        },
                                                    })}
                                                >Edit</a>
                                            </div>
                                        )}
                                    </div>

                                    {officeAddress.value === address && (
                                        <div className="line-type">
                                            {Badge({
                                                color: "blue",
                                                text: "Office Address",
                                                xdQaName: "Office Address"
                                            })}
                                        </div>
                                    )}

                                    {LineDetails({address})}

                                    {Actions({different: true})}
                                </div>
                            ),
                            xdQaName: address.address1
                        })
                    )}

                    {(loading || validation.loading) ? (
                        LoadingPanel({})
                    ) : !validation.value && (
                        <div className="error">
                            Invalid shipping address
                        </div>
                    )}
                </div>
            );
        },
    }),
);

const LineDetails = ({address}) => (
    <div className="line-details">
        <div className="col">
            <div className="label" xd-qa-type="label" xd-qa-name="Details">
                Details
            </div>

            <div className="value">
                <div className="line" xd-qa-type="label" xd-qa-name="address1">
                    {address.address1}
                </div>
                {address.address2 && (
                    <div className="line" xd-qa-type="label" xd-qa-name="address2">
                        {address.address2}
                    </div>
                )}
                <div className="line" xd-qa-type="label" xd-qa-name="cityStateZip">
                    {address.city}, {address.state} {address.postalCode}
                </div>
            </div>
        </div>
        <div className="col">
            <div className="label" xd-qa-type="label" xd-qa-name="Company">
                Company
            </div>

            <div className="value">
                <div className="line" xd-qa-type="label" xd-qa-name="CompanyValue">
                    {address.company}
                </div>
            </div>
        </div>
        <div className="col">
            <div className="label" xd-qa-type="label" xd-qa-name="Phone">
                Phone
            </div>

            <div className="value">
                <div className="line" xd-qa-type="label" xd-qa-name="PhoneValue">
                    {address.contactPhone}
                </div>
            </div>
        </div>
    </div>
);
